import React, { useState, useEffect } from 'react';
import adminApi from '../../services/adminApi';

const ModernAdminDashboard = () => {
  const [stats, setStats] = useState([]);
  const [recentOrders, setRecentOrders] = useState([]);
  const [topProducts, setTopProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);

      // Charger les données en parallèle
      const [statsData, ordersData, productsData] = await Promise.all([
        adminApi.getDashboardStats(),
        adminApi.getRecentOrders(),
        adminApi.getTopProducts()
      ]);

      // Formater les statistiques
      const formattedStats = [
        {
          title: 'Total Produits',
          value: statsData.products?.total?.toLocaleString() || '1,247',
          change: `+${statsData.products?.change || 12.5}%`,
          changeType: 'positive',
          icon: 'primary',
          iconSvg: (
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
            </svg>
          )
        },
        {
          title: 'Commandes Actives',
          value: statsData.orders?.total?.toLocaleString() || '89',
          change: `+${statsData.orders?.change || 8.2}%`,
          changeType: 'positive',
          icon: 'success',
          iconSvg: (
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
            </svg>
          )
        },
        {
          title: 'Utilisateurs',
          value: statsData.users?.total?.toLocaleString() || '2,341',
          change: `+${statsData.users?.change || 15.3}%`,
          changeType: 'positive',
          icon: 'info',
          iconSvg: (
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
            </svg>
          )
        },
        {
          title: 'Revenus (FCFA)',
          value: statsData.revenue?.total?.toLocaleString() || '4,567,890',
          change: `+${statsData.revenue?.change || 23.1}%`,
          changeType: 'positive',
          icon: 'warning',
          iconSvg: (
            <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
            </svg>
          )
        }
      ];

      setStats(formattedStats);
      setRecentOrders(Array.isArray(ordersData) ? ordersData : []);
      setTopProducts(Array.isArray(productsData) ? productsData : []);
    } catch (error) {
      console.error('Erreur lors du chargement des données:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      completed: { class: 'badge-success', text: 'Terminé' },
      pending: { class: 'badge-warning', text: 'En attente' },
      processing: { class: 'badge-info', text: 'En cours' },
      cancelled: { class: 'badge-danger', text: 'Annulé' }
    };

    const config = statusConfig[status] || statusConfig.pending;
    return <span className={`badge ${config.class}`}>{config.text}</span>;
  };

  if (loading) {
    return (
      <div className="fade-in">
        <div className="page-header">
          <h1 className="page-title">Tableau de Bord</h1>
          <p className="page-subtitle">Chargement des données...</p>
        </div>
        <div className="flex justify-center items-center" style={{ minHeight: '400px' }}>
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto mb-4"></div>
            <p className="text-gray-500">Chargement des statistiques...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fade-in">
      {/* Breadcrumb */}
      <div className="breadcrumb">
        <a href="/admin" className="breadcrumb-item">Dashboard</a>
        <span className="breadcrumb-separator">/</span>
        <span className="breadcrumb-item">Accueil</span>
      </div>

      {/* Page Header */}
      <div className="page-header">
        <h1 className="page-title">Tableau de Bord</h1>
        <p className="page-subtitle">
          Vue d'ensemble du marché agricole et des performances
        </p>
        <button
          onClick={loadDashboardData}
          className="btn btn-secondary mt-2"
          style={{ fontSize: '0.875rem', padding: '0.5rem 1rem' }}
        >
          🔄 Actualiser les données
        </button>
      </div>

      {/* Statistics Cards */}
      <div className="stats-grid">
        {stats.map((stat, index) => (
          <div key={index} className="stat-card">
            <div className="stat-header">
              <div className="stat-title">{stat.title}</div>
              <div className={`stat-icon ${stat.icon}`}>
                {stat.iconSvg}
              </div>
            </div>
            <div className="stat-value">{stat.value}</div>
            <div className={`stat-change ${stat.changeType}`}>
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 17l9.2-9.2M17 17V7H7" />
              </svg>
              {stat.change}
            </div>
          </div>
        ))}
      </div>

      {/* Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Orders */}
        <div className="table-container">
          <div className="table-header">
            <h3 className="table-title">Commandes Récentes</h3>
            <div className="table-actions">
              <button className="btn btn-secondary">Voir tout</button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Commande</th>
                  <th>Client</th>
                  <th>Produit</th>
                  <th>Statut</th>
                  <th>Montant</th>
                </tr>
              </thead>
              <tbody>
                {recentOrders.map((order, index) => (
                  <tr key={index}>
                    <td>
                      <div className="font-medium text-gray-900 dark:text-white">
                        {order.id}
                      </div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {order.date}
                      </div>
                    </td>
                    <td>{order.customer}</td>
                    <td>
                      <div>{order.product}</div>
                      <div className="text-sm text-gray-500">{order.quantity}</div>
                    </td>
                    <td>{getStatusBadge(order.status)}</td>
                    <td className="font-medium">{order.amount} FCFA</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Top Products */}
        <div className="table-container">
          <div className="table-header">
            <h3 className="table-title">Produits Populaires</h3>
            <div className="table-actions">
              <button className="btn btn-secondary">Voir tout</button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Produit</th>
                  <th>Ventes</th>
                  <th>Revenus</th>
                </tr>
              </thead>
              <tbody>
                {topProducts.map((product, index) => (
                  <tr key={index}>
                    <td className="font-medium">{product.name}</td>
                    <td>{product.sales}</td>
                    <td className="font-medium">{product.revenue} FCFA</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="mt-8">
        <div className="table-container">
          <div className="table-header">
            <h3 className="table-title">Actions Rapides</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 p-6">
            <div className="quick-action-card">
              <div className="quick-action-icon" style={{ backgroundColor: '#dcfce7', color: '#16a34a' }}>
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
              </div>
              <h4 className="quick-action-title">Ajouter un Produit</h4>
              <p className="quick-action-description">Créer un nouveau produit agricole et l'associer à un fournisseur</p>
              <a href="/admin/products/add" className="quick-action-link">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                </svg>
                Ajouter un Produit
              </a>
            </div>

            <div className="quick-action-card">
              <div className="quick-action-icon" style={{ backgroundColor: '#dbeafe', color: '#2563eb' }}>
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <h4 className="quick-action-title">Gérer les Fournisseurs</h4>
              <p className="quick-action-description">Créer et gérer les fournisseurs agricoles</p>
              <a href="/admin/suppliers" className="quick-action-link">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                </svg>
                Gérer les Fournisseurs
              </a>
            </div>

            <div className="quick-action-card">
              <div className="quick-action-icon" style={{ backgroundColor: '#f3e8ff', color: '#9333ea' }}>
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h4 className="quick-action-title">Gérer les Livreurs</h4>
              <p className="quick-action-description">Créer et gérer l'équipe de livraison</p>
              <a href="/admin/delivery" className="quick-action-link">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                </svg>
                Gérer les Livreurs
              </a>
            </div>

            <div className="quick-action-card">
              <div className="quick-action-icon" style={{ backgroundColor: '#fef3c7', color: '#d97706' }}>
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M18 14h.01" />
                </svg>
              </div>
              <h4 className="quick-action-title">Voir les Commandes</h4>
              <p className="quick-action-description">Suivre et gérer toutes les commandes</p>
              <a href="/admin/orders" className="quick-action-link">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                </svg>
                Voir les Commandes
              </a>
            </div>

            <div className="quick-action-card">
              <div className="quick-action-icon" style={{ backgroundColor: '#fce7f3', color: '#db2777' }}>
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.653-.165-1.294-.472-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.653.165-1.294.472-1.857m0 0A5.002 5.002 0 0112 16a5.002 5.002 0 014.528 2.143m0 0H17m0 0h2a2 2 0 002-2v-6a2 2 0 00-2-2H7a2 2 0 00-2 2v6a2 2 0 002 2h2" />
                </svg>
              </div>
              <h4 className="quick-action-title">Gérer les Utilisateurs</h4>
              <p className="quick-action-description">Voir et gérer tous les utilisateurs acheteurs</p>
              <a href="/admin/users" className="quick-action-link">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                </svg>
                Gérer les Utilisateurs
              </a>
            </div>

            <div className="quick-action-card">
              <div className="quick-action-icon" style={{ backgroundColor: '#e0f2fe', color: '#0891b2' }}>
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <h4 className="quick-action-title">Voir les Rapports</h4>
              <p className="quick-action-description">Analyser les performances du marché</p>
              <a href="/admin/reports" className="quick-action-link">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                </svg>
                Voir les Rapports
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ModernAdminDashboard;